import { useState, useEffect, useCallback } from "react";
import { usePermissions } from "./PermissionsContext";
import { fmt, statusColor } from "./Utils/Utilities";
import { RevenueChart, CollectionBarChart, BarChart, Badge } from "./SharedUI";
import RentalAgingPage from "./Rentalaging";
import AllocationHistoryPage from "./Allocationhistory";
import "./Main.css";

// ─── Constants ────────────────────────────────────────────────────────────────
const REPORT_TYPES = ["Allocation"];

const EXPENSE_KPIS = [
  {
    label: "Total Expense",
    value: "R2.85M",
    trend: "+12.4%",
    up: true,
    note: "vs prior year",
  },
  {
    label: "Avg Monthly",
    value: "R237k",
    trend: "+8.1%",
    up: true,
    note: "rolling 6 months",
  },
  {
    label: "Current Month",
    value: "R354k",
    trend: "2026/05/07",
    up: true,
    note: "all-time high",
  },
];

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE =
  process.env.REACT_APP_API_BASE || "https://api.seritonex.com";
const getToken = () => localStorage.getItem("token") || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  accept: "application/json",
  ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
});

const decodeToken = () => {
  try {
    const jwt = getToken();
    if (!jwt) return null;
    const payload = jwt.split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
};

const getCurrentUserId = () => {
  const payload = decodeToken();
  const raw = payload?.user_id ?? payload?.id ?? payload?.sub ?? null;
  const n = raw != null ? Number(raw) : NaN;
  return !isNaN(n) && n > 0 ? n : null;
};

async function apiFetch(method, path, body = null) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: authHeaders(),
      ...(body !== null ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (e) {
    console.error(`apiFetch ${method} ${path}:`, e);
    throw e;
  }
}

// ─── Rental aging item (styled like allocation rows) ──────────────────────────
function RentalRow({ r }) {
  return (
    <div className="rpt-alloc-row">
      <div className="rpt-alloc-summary">
        <div className="rpt-alloc-left">
          <div>
            <div className="fs12 fw6">{r.asset}</div>
            <div className="fs10 tc3 mono">
              {r.employee} {r.employeeId ? `· ${r.employeeId}` : ""}
            </div>
          </div>
        </div>
        <div className="rpt-alloc-meta">
          <div className="rpt-alloc-chip">
            <span className="fs10 tc3">Days</span>
            <span className="fs13 fw7 mono">{r.daysOut ?? "—"}</span>
          </div>
          <div className="rpt-alloc-chip">
            <span className="fs10 tc3">Site</span>
            <span className="fs13 fw7 mono">{r.site ?? "—"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const fetchAllocationSummary = (
  startDate,
  endDate,
  isSupplier = false,
  branchId = 0,
  warehouseId = 0,
) =>
  apiFetch("POST", "/api/v1/amis/asset/allocation-summary", {
    startDate: new Date(startDate).toISOString(),
    endDate: new Date(endDate).toISOString(),
    is_supplier: isSupplier,
    branch_id: branchId,
    warehouse_id: warehouseId,
  });

const fetchAllocationBreakdown = (
  startDate,
  endDate,
  isSupplier = false,
  warehouseId = 0,
  branchId = 0,
) =>
  apiFetch("POST", "/api/v1/amis/asset/allocation-breakdown", {
    startDate: new Date(startDate).toISOString(),
    endDate: new Date(endDate).toISOString(),
    is_supplier: isSupplier,
    warehouse_id: warehouseId,
    branch_id: branchId,
  });

const fetchBranchesByUser = (user_id) =>
  apiFetch("POST", "/api/v1/amis/branch/by-user-id", { user_id });

const fetchWarehousesByUser = (user_id) =>
  apiFetch("POST", "/api/v1/amis/warehouse/by-user", { user_id });

// ─── Helpers ──────────────────────────────────────────────────────────────────
function today() {
  return new Date().toISOString().slice(0, 10);
}
function yearStart() {
  return `${new Date().getFullYear()}-01-01`;
}

// ─── CSV Export ───────────────────────────────────────────────────────────────
function downloadCSV(filename, rows, columns) {
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((row) =>
      columns
        .map((c) => {
          const val = row[c.key] ?? "";
          const str = String(val).replace(/"/g, '""');
          return /[,"\n]/.test(str) ? `"${str}"` : str;
        })
        .join(","),
    )
    .join("\n");
  const blob = new Blob([`${header}\n${body}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement("a"), {
    href: url,
    download: filename,
  }).click();
  URL.revokeObjectURL(url);
}

const ALLOC_BREAKDOWN_COLS = [
  { label: "Code", key: "asset_type_code" },
  { label: "Serial Number", key: "serial_number" },
  { label: "Description", key: "asset_description" },
  { label: "Warehouse", key: "warehouse_name" },
  { label: "Branch", key: "branch_name" },
  { label: "Days", key: "rental_days" },
  { label: "Rate (R/d)", key: "rental_rate" },
  { label: "Charge (excl)", key: "rental_charge" },
];

// ─── Normaliser ───────────────────────────────────────────────────────────────
function normaliseAllocation(raw = []) {
  const list = Array.isArray(raw)
    ? raw
    : (raw?.summary ?? raw?.data ?? raw?.items ?? []);

  if (!Array.isArray(list) || list.length === 0) return null;

  return list.map((item) => ({
    asset_type_code: item.asset_type_code ?? "—",
    asset_description: item.asset_description ?? item.name ?? "Unknown",
    warehouse_name: item.warehouse_name ?? "—",
    warehouse_id: item.warehouse_id ?? item.warehouseId ?? 0,
    branch_name: item.branch_name ?? "—",
    branch_id: item.branch_id ?? item.branchId ?? 0,
    organization_name: item.organization_name ?? "—",
    quantity: Number(item.quantity ?? 0),
    rental_rate: item.rental_rate ?? "0.00",
    combined_rental_days: item.combined_rental_days ?? "0",
    charge_excl: Number(item.charge_excl ?? 0),
  }));
}

// ─── Shared sub-components ────────────────────────────────────────────────────
function KPICard({ label, value, trend, up, note }) {
  return (
    <div className={`rpt-kpi-card ${up ? "rpt-kpi-up" : "rpt-kpi-dn"}`}>
      <div className="rpt-kpi-label">{label}</div>
      <div className="rpt-kpi-value">{value}</div>
      <div className="rpt-kpi-footer">
        <span className={up ? "rpt-trend-up" : "rpt-trend-dn"}>{trend}</span>
        {note && <span className="rpt-kpi-note">{note}</span>}
      </div>
    </div>
  );
}

function ChartPanel({ title, subtitle, children, action }) {
  return (
    <div className="panel">
      <div className="ph">
        <div className="rpt-ph-inner">
          <div className="ph-title">{title}</div>
          {subtitle && <div className="ph-sub">{subtitle}</div>}
        </div>
        {action}
      </div>
      <div className="pb">{children}</div>
    </div>
  );
}

// ─── Date range picker ────────────────────────────────────────────────────────
function DateRangePicker({ startDate, endDate, onStartChange, onEndChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 11, color: "var(--tc3)" }}>From</span>
      <input
        type="date"
        className="tfilter"
        value={startDate}
        max={endDate}
        onChange={(e) => onStartChange(e.target.value)}
        style={{ fontSize: 12 }}
      />
      <span style={{ fontSize: 11, color: "var(--tc3)" }}>To</span>
      <input
        type="date"
        className="tfilter"
        value={endDate}
        min={startDate}
        onChange={(e) => onEndChange(e.target.value)}
        style={{ fontSize: 12 }}
      />
    </div>
  );
}

// ─── Expense tab ──────────────────────────────────────────────────────────────
function ExpenseReport({ rentals = [] }) {
  const overdueRentals = Array.isArray(rentals)
    ? rentals.filter((r) => r.status === "Overdue")
    : [];

  return (
    <>
      <div className="rpt-kpi-grid">
        {EXPENSE_KPIS.map((k) => (
          <KPICard key={k.label} {...k} />
        ))}
      </div>

      <div className="g2 rpt-snapshot-row">
        <ChartPanel
          title="Rental Aging Snapshot"
          subtitle="Quick rental exposure preview"
        >
          <div className="rpt-snapshot-body">
            <div className="fs11 fw6 rpt-snapshot-heading">
              Overdue rentals: {overdueRentals.length}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginTop: 8,
              }}
            >
              {overdueRentals.length === 0 && (
                <div className="fs11 tc3" style={{ padding: "12px 0" }}>
                  No overdue rentals
                </div>
              )}
              {overdueRentals.slice(0, 6).map((r, i) => (
                <RentalRow key={`${r.asset}-${r.employeeId ?? i}`} r={r} />
              ))}
            </div>
          </div>
        </ChartPanel>
      </div>
    </>
  );
}

// ─── Allocation row ───────────────────────────────────────────────────────────
function AllocationRow({ item }) {
  return (
    <div className="rpt-alloc-row">
      <div className="rpt-alloc-summary">
        <div className="rpt-alloc-left">
          <div>
            <div className="fs12 fw6">{item.asset_description}</div>
            <div className="fs10 tc3 mono">{item.asset_type_code}</div>
          </div>
        </div>

        <div className="rpt-alloc-meta">
          <div className="rpt-alloc-chip">
            <span className="fs10 tc3">Qty</span>
            <span className="fs13 fw7 mono">{fmt.number(item.quantity)}</span>
          </div>
          <div className="rpt-alloc-chip">
            <span className="fs10 tc3">Days</span>
            <span className="fs13 fw7 mono">{item.combined_rental_days}</span>
          </div>
          <div className="rpt-alloc-chip">
            <span className="fs10 tc3">Rate</span>
            <span className="fs13 fw7 mono">R{item.rental_rate}/d</span>
          </div>
          <div className="rpt-alloc-chip rpt-alloc-charge">
            <span className="fs10 tc3">Charge (excl)</span>
            <span className="fs13 fw7 mono">
              {fmt.currency(item.charge_excl)}
            </span>
          </div>
        </div>
      </div>

      <div className="rpt-alloc-tags">
        <span className="rpt-tag">{item.warehouse_name}</span>
        <span className="rpt-tag">{item.branch_name}</span>
        <span className="rpt-tag">{item.organization_name}</span>
      </div>
    </div>
  );
}

// ─── Breakdown inline table ───────────────────────────────────────────────────
function BreakdownTable({ rows, loading, error }) {
  if (loading) {
    return (
      <div
        style={{
          padding: "32px 0",
          textAlign: "center",
          color: "var(--tc3)",
          fontSize: 13,
        }}
      >
        <i className="bi bi-hourglass-split" style={{ marginRight: 8 }} />
        Loading breakdown…
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: "32px 0",
          textAlign: "center",
          color: "var(--danger)",
          fontSize: 13,
        }}
      >
        ⚠️ {error}
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div
        style={{
          padding: "32px 0",
          textAlign: "center",
          color: "var(--tc3)",
          fontSize: 13,
        }}
      >
        No breakdown data for the selected filters
      </div>
    );
  }

  return (
    <table className="tbl" style={{ width: "100%" }}>
      <thead>
        <tr>
          <th style={{ width: 48 }}>#</th>
          <th>Code</th>
          <th>S/N</th>
          <th>Description</th>
          <th>Warehouse</th>
          <th>Branch</th>
          <th style={{ textAlign: "right" }}>Days</th>
          <th style={{ textAlign: "right" }}>Rate</th>
          <th style={{ textAlign: "right" }}>Charge (excl)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, idx) => (
          <tr key={`${r.warehouse_stock_id ?? idx}-${idx}`}>
            <td className="fs11 tc2" style={{ whiteSpace: "nowrap" }}>
              {idx + 1}
            </td>
            <td className="mono fs11 t-flame" style={{ whiteSpace: "nowrap" }}>
              {r.asset_type_code ?? "—"}
            </td>
            <td className="fs11 mono">{r.serial_number ?? "—"}</td>
            <td className="fs12">{r.asset_description ?? "—"}</td>
            <td className="fs11 tc2">{r.warehouse_name ?? "—"}</td>
            <td className="fs11 tc2">{r.branch_name ?? "—"}</td>
            <td className="mono fs12" style={{ textAlign: "right" }}>
              {r.rental_days ?? "—"}
            </td>
            <td className="mono fs12" style={{ textAlign: "right" }}>
              {r.rental_rate ? `R${r.rental_rate}/d` : "—"}
            </td>
            <td
              className="mono fs12 fw6"
              style={{ textAlign: "right", whiteSpace: "nowrap" }}
            >
              {fmt.currency(Number(r.rental_charge) || 0)}
            </td>
          </tr>
        ))}
        {/* totals row */}
        <tr style={{ borderTop: "2px solid var(--border)" }}>
          <td colSpan={4} />
          <td
            colSpan={2}
            style={{
              textAlign: "right",
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            Totals
          </td>
          <td className="mono fs12 fw7" style={{ textAlign: "right" }}>
            {rows.reduce((s, x) => s + (Number(x.rental_days) || 0), 0)}
          </td>
          <td />
          <td
            className="mono fs12 fw7"
            style={{ textAlign: "right", whiteSpace: "nowrap" }}
          >
            {fmt.currency(
              rows.reduce((s, x) => s + (Number(x.rental_charge) || 0), 0),
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

// ─── Summary table for allocation summary (desktop style) ────────────────────
function SummaryTable({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div style={{ padding: 20, color: "var(--tc3)" }}>
        No allocation summary data
      </div>
    );
  }

  const totalQty = rows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
  const totalCharge = rows.reduce(
    (s, r) => s + (Number(r.charge_excl) || 0),
    0,
  );

  return (
    <table className="tbl" style={{ width: "100%" }}>
      <thead>
        <tr>
          <th>Code</th>
          <th>Description</th>
          <th>Warehouse</th>
          <th>Branch</th>
          <th>Organization</th>
          <th style={{ textAlign: "right" }}>Qty</th>
          <th>Charge (Excl)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, idx) => (
          <tr key={`${r.asset_type_code ?? idx}-${idx}`}>
            <td className="mono fs11 t-flame">{r.asset_type_code ?? "—"}</td>
            <td className="fs12">{r.asset_description ?? "—"}</td>
            <td className="fs11 tc2">{r.warehouse_name ?? "—"}</td>
            <td className="fs11 tc2">{r.branch_name ?? "—"}</td>
            <td className="fs11 tc2">{r.organization_name ?? "—"}</td>
            <td className="mono fs12" style={{ textAlign: "right" }}>
              {fmt.number(r.quantity)}
            </td>
            <td
              className="fs11 tc2"
              style={{
                marginLeft: "60px",
                textAlign: "right",
                padding: "5px, 5px, 5px, 5px",
              }}
            >
              {fmt.currency(r.charge_excl)}
            </td>
          </tr>
        ))}
        <tr>
          <td colSpan={5} style={{ textAlign: "right", fontWeight: 700 }}>
            Totals
          </td>
          <td
            className="mono fs12"
            style={{ textAlign: "right", fontWeight: 700 }}
          >
            {fmt.number(totalQty)}
          </td>
          <td
            className="mono fs12"
            style={{ textAlign: "right", fontWeight: 700 }}
          >
            {fmt.currency(totalCharge)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

// ─── Allocation tab ───────────────────────────────────────────────────────────
function AllocationReport({
  startDate,
  endDate,
  selectedBranch,
  selectedWarehouse,
  showBreakdown,
  refreshKey,
  onLoadingChange,
  onBreakdownRowsChange,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSupplier] = useState(false);

  const [breakdownRows, setBreakdownRows] = useState([]);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState(null);

  useEffect(() => {
    onLoadingChange?.(loading || breakdownLoading);
  }, [loading, breakdownLoading, onLoadingChange]);

  const loadSummary = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllocationSummary(
        startDate,
        endDate,
        isSupplier,
        selectedBranch,
        selectedWarehouse,
      );
      setItems(normaliseAllocation(data) ?? []);
    } catch (err) {
      console.error("Allocation summary error:", err);
      setError(err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, isSupplier, selectedBranch, selectedWarehouse]);

  const loadBreakdown = useCallback(async () => {
    if (!startDate || !endDate) return;
    setBreakdownLoading(true);
    setBreakdownError(null);
    try {
      const res = await fetchAllocationBreakdown(
        startDate,
        endDate,
        isSupplier,
        selectedWarehouse || 0,
        selectedBranch || 0,
      );
      const rows = Array.isArray(res)
        ? res
        : (res?.summary ?? res?.data ?? res?.items ?? res?.breakdown ?? []);
      setBreakdownRows(rows);
      onBreakdownRowsChange?.(rows);
    } catch (err) {
      console.error("Allocation breakdown error:", err);
      setBreakdownError(err.message || "Failed to load breakdown");
      setBreakdownRows([]);
      onBreakdownRowsChange?.([]);
    } finally {
      setBreakdownLoading(false);
    }
  }, [startDate, endDate, isSupplier, selectedBranch, selectedWarehouse]);

  useEffect(() => {
    if (!showBreakdown) loadSummary();
  }, [loadSummary, showBreakdown, refreshKey]);

  useEffect(() => {
    if (showBreakdown) loadBreakdown();
  }, [loadBreakdown, showBreakdown, refreshKey]);

  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const totalCharge = items.reduce((s, i) => s + i.charge_excl, 0);

  return (
    <div className="panel">
      <div className="ph">
        <div>
          <div className="ph-title">
            Allocation {showBreakdown ? "Breakdown" : "Summary"}
          </div>
          <div className="ph-sub">
            {loading || breakdownLoading ? (
              "Loading…"
            ) : error ? (
              <span style={{ color: "var(--danger)" }}>
                Error — showing cached data
              </span>
            ) : showBreakdown ? (
              `${breakdownRows.length} line items`
            ) : (
              `${items.length} asset types · ${fmt.number(totalQty)} units · ${fmt.currency(totalCharge)} total`
            )}
          </div>
        </div>
      </div>

      <div className="pb1" style={showBreakdown ? { padding: 0 } : {}}>
        {showBreakdown ? (
          <BreakdownTable
            rows={breakdownRows}
            loading={breakdownLoading}
            error={breakdownError}
          />
        ) : loading && items.length === 0 ? (
          <div
            style={{
              padding: "32px 0",
              textAlign: "center",
              color: "var(--tc3)",
              fontSize: 13,
            }}
          >
            <i className="bi bi-hourglass-split" style={{ marginRight: 8 }} />
            Loading allocation data…
          </div>
        ) : (
          <div className="rpt-alloc-list" style={{ padding: "0px" }}>
            <SummaryTable rows={items} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page shell ──────────────────────────────────────────────────────────────
function ReportingPage({ showToast: toast }) {
  const [reportType, setReportType] = useState("Allocation");
  const [startDate, setStartDate] = useState(yearStart());
  const [endDate, setEndDate] = useState(today());
  const [subPage, setSubPage] = useState(null);
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(0);
  const [selectedWarehouse, setSelectedWarehouse] = useState(0);
  const [rentals, setRentals] = useState([]);
  const [rentalsLoading, setRentalsLoading] = useState(false);

  // ── Allocation top-bar controls ───────────────────────────────────────────
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [allocationLoading, setAllocationLoading] = useState(false);
  const [breakdownRows, setBreakdownRows] = useState([]);

  /*useEffect(() => {
    let mounted = true;
    setRentalsLoading(true);
    getRentalAging()
      .then((res) => {
        const arr = Array.isArray(res) ? res : res?.data || res?.rentals || [];
        if (mounted) setRentals(Array.isArray(arr) ? arr : []);
      })
      .catch(() => {
        if (mounted) setRentals([]);
      })
      .finally(() => {
        if (mounted) setRentalsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);
*/
  const loadBranches = useCallback(async () => {
    const userId = getCurrentUserId();
    if (!userId) return;
    try {
      const raw = await fetchBranchesByUser(userId);
      let list = [];
      if (Array.isArray(raw)) list = raw;
      else if (Array.isArray(raw?.branches)) list = raw.branches;
      else if (Array.isArray(raw?.data)) list = raw.data;
      else if (
        raw &&
        typeof raw === "object" &&
        (raw.branch_id != null || raw.id != null)
      )
        list = [raw];
      setBranches(list);
      if (list.length > 0) {
        const first = list[0];
        setSelectedBranch(first.branch_id ?? first.id ?? 0);
      }
    } catch {
      setBranches([]);
    }
  }, []);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  // Re-fetch warehouses when the selected branch changes
  useEffect(() => {
    const userId = getCurrentUserId();
    if (!userId) {
      setWarehouses([]);
      setSelectedWarehouse(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const raw = await fetchWarehousesByUser(userId);
        let list = [];
        if (Array.isArray(raw)) list = raw;
        else if (Array.isArray(raw?.warehouses)) list = raw.warehouses;
        else if (Array.isArray(raw?.data)) list = raw.data;
        else if (
          raw &&
          typeof raw === "object" &&
          (raw.warehouse_id != null || raw.id != null)
        )
          list = [raw];
        if (!cancelled) setWarehouses(list);
      } catch {
        if (!cancelled) setWarehouses([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBranch]);

  const { canSeePage, ready } = usePermissions();

  if (ready && !canSeePage("reporting")) {
    return (
      <div className="panel" style={{ padding: 24 }}>
        <h2>Access denied</h2>
        <p className="tc3">You do not have permission to view reports.</p>
      </div>
    );
  }

  /*if (subPage === "rental-aging")
    return (
      <RentalAgingPage showToast={toast} onBack={() => setSubPage(null)} />
    );
  if (subPage === "allocation-history")
    return (
      <AllocationHistoryPage
        showToast={toast}
        onBack={() => setSubPage(null)}
      />
    );*/

  return (
    <div className="page">
      {/* ── Top bar ── */}
      <div className="fb mb22" style={{ flexWrap: "wrap", gap: 12 }}>
        <div className="tabs">
          {REPORT_TYPES.map((r) => (
            <div
              key={r}
              className={`tab ${reportType === r ? "on" : ""}`}
              onClick={() => setReportType(r)}
            >
              {r}
            </div>
          ))}
        </div>

        <div className="rpt-topbar-actions">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartChange={setStartDate}
            onEndChange={setEndDate}
          />

          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "var(--tc3)" }}>Branch</span>
            <select
              value={selectedBranch}
              onChange={(e) => {
                setSelectedBranch(Number(e.target.value || 0));
                setSelectedWarehouse(0);
              }}
              className="tfilter"
              style={{ width: 180, fontSize: 12 }}
            >
              <option value={0}>All branches</option>
              {branches.map((branch) => {
                const id = branch.branch_id ?? branch.id ?? 0;
                const name =
                  branch.branch_name ??
                  branch.name ??
                  branch.display_name ??
                  `Branch ${id}`;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "var(--tc3)" }}>Warehouse</span>
            <select
              value={selectedWarehouse}
              onChange={(e) =>
                setSelectedWarehouse(Number(e.target.value || 0))
              }
              className="tfilter"
              style={{ width: 180, fontSize: 12 }}
            >
              <option value={0}>All warehouses</option>
              {warehouses.map((warehouse) => {
                const id = warehouse.warehouse_id ?? warehouse.id ?? 0;
                const name =
                  warehouse.warehouse_name ??
                  warehouse.name ??
                  warehouse.display_name ??
                  `Warehouse ${id}`;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </label>

          {/* ── Breakdown / Refresh — only shown for Allocation tab ── */}
          {reportType === "Allocation" && (
            <>
              <button
                className="btn btn-s"
                onClick={() => {
                  setShowBreakdown((prev) => !prev);
                  if (showBreakdown) setBreakdownRows([]);
                }}
                title={
                  showBreakdown ? "Back to summary" : "Show full breakdown"
                }
              >
                <i
                  className={`bi bi-${showBreakdown ? "list-ul" : "list-nested"}`}
                />
                <span style={{ marginLeft: 5 }}>
                  {showBreakdown ? "Summary" : "Breakdown"}
                </span>
              </button>

              {showBreakdown && (
                <button
                  className="btn btn-s"
                  onClick={() =>
                    downloadCSV(
                      `allocation-breakdown-${new Date().toISOString().slice(0, 10)}.csv`,
                      breakdownRows,
                      ALLOC_BREAKDOWN_COLS,
                    )
                  }
                  title="Export breakdown as CSV"
                  style={{ display: "flex", alignItems: "center", gap: 5 }}
                  disabled={allocationLoading || breakdownRows.length === 0}
                >
                  <i className="bi bi-download" style={{ fontSize: 12 }} />
                  <span style={{ marginLeft: 4 }}>CSV</span>
                </button>
              )}

              <button
                className="btn btn-s"
                onClick={() => setRefreshKey((k) => k + 1)}
                disabled={allocationLoading}
                title="Refresh"
              >
                <i
                  className={`bi bi-arrow-clockwise${allocationLoading ? " spin" : ""}`}
                />
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Active report ── */}
      {reportType === "Expense" && <ExpenseReport rentals={rentals} />}
      {reportType === "Allocation" && (
        <AllocationReport
          startDate={startDate}
          endDate={endDate}
          selectedBranch={selectedBranch}
          selectedWarehouse={selectedWarehouse}
          showBreakdown={showBreakdown}
          refreshKey={refreshKey}
          onLoadingChange={setAllocationLoading}
          onBreakdownRowsChange={setBreakdownRows}
        />
      )}
    </div>
  );
}

export default ReportingPage;
